import { useEffect } from "react";
import { U2000ParseResponseError, u2000AxiosNonAuthenticated } from "./U2000Axios.js";
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import { useU2000BusyIndicatorContext } from "../context/busiIndicator/U2000BusyIndicatorContext.js";

const useU2000AxiosNonAuthenticated = (): AxiosInstance => {
  const { showBusyIndicator, hideBusyIndicator } = useU2000BusyIndicatorContext();

  useEffect(() => {
    const requestIntercept = u2000AxiosNonAuthenticated.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        showBusyIndicator();
        if (import.meta.env.DEV) {
          console.debug(`(AXIOS) : ${config.method?.toUpperCase() || "UNKNOWN"} request sent to ${config.url} with params ${JSON.stringify(config.params)}`);
        }
        return config;
      },
      (error: AxiosError) => {
        hideBusyIndicator();
        return Promise.reject(error);
      }
    );

    const responseIntercept = u2000AxiosNonAuthenticated.interceptors.response.use(
      (response) => {
        if (import.meta.env.DEV) {
          console.debug(
            `(AXIOS) : ${response.config.method?.toUpperCase() || "UNKNOWN"} response from ${response.config.url} with status code ${response.status} and Data ${JSON.stringify(response.data)}`
          );
        }
        hideBusyIndicator();
        return response;
      },
      async (error: AxiosError) => {
        if (error.response) {
          const u2000Error = U2000ParseResponseError(error);
          if (import.meta.env.DEV) console.debug(`(AXIOS RESPONSE ERROR) : ${JSON.stringify(u2000Error)}`);
          hideBusyIndicator();
          return Promise.reject(u2000Error);
        } else if (error.request) {
          // The request was made, but no response was received
          console.error(error.request);
        } else {
          // Something happened setting up the request
          console.error("Error", error.message);
        }
        hideBusyIndicator();
        return Promise.reject(error);
      }
    );

    return () => {
      u2000AxiosNonAuthenticated.interceptors.request.eject(requestIntercept);
      u2000AxiosNonAuthenticated.interceptors.response.eject(responseIntercept);
    };
  }, []);

  return u2000AxiosNonAuthenticated;
};

export default useU2000AxiosNonAuthenticated;
